import { defineStore } from 'pinia'
import { computed, nextTick, ref, unref, watch } from 'vue'
import { useAuth0, getDefaultAuth0Config, isKoraAdminTrustLevel } from '@architeia/auth'
import { createKoraClient } from '@architeia/api-client'
import { resolveKoraAccountIdentity } from '@architeia/kora-account-menu'
import type { KoraAccountIdentity } from '@architeia/kora-account-menu'
import { useAccountDeletionStore } from '@/stores/accountDeletion'
import { useAccountPreferencesStore } from '@/stores/accountPreferences'

const ACCOUNT_CLOSED_CODE = 'KORA_ACCOUNT_CLOSED'
const SIGNUP_NOT_INVITED_CODE = 'KORA_SIGNUP_NOT_INVITED'
const EMAIL_NOT_VERIFIED_CODE = 'KORA_EMAIL_NOT_VERIFIED'

/** KORA-125: Kora refuses a token of an account that is being deleted or has been deleted. */
function isAccountClosedError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === ACCOUNT_CLOSED_CODE
  )
}

/** KORA-148: Kora creates users only for invited or imported members and refuses any other sign-in. */
function isSignupNotInvitedError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === SIGNUP_NOT_INVITED_CODE
  )
}

/** KORA-152: Kora links an invited member's account only once the sign-in's email is verified. */
function isEmailNotVerifiedError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === EMAIL_NOT_VERIFIED_CODE
  )
}

export const useAuthStore = defineStore('auth', () => {
  // (D7, KORA-66) getDefaultAuth0Config() throws if VITE_AUTH0_DOMAIN/VITE_AUTH0_CLIENT_ID
  // were empty at build time. That produces the same symptom as the unbounded-init hang
  // (blank page, zero Auth0 requests) via a different mechanism. Fall back to an inert
  // sentinel config so useAuth0() still returns a live reactive surface instead of the
  // whole store constructor throwing.
  const authConfigError = ref<Error | null>(null)
  let auth0Config: ReturnType<typeof getDefaultAuth0Config>
  try {
    auth0Config = getDefaultAuth0Config()
  } catch (err) {
    authConfigError.value = err instanceof Error ? err : new Error('Failed to load Auth0 config')
    auth0Config = { domain: '', clientId: '', audience: '' }
  }

  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
    getAccessToken,
    handleRedirectCallback,
    initialize,
    initTimedOut
  } = useAuth0(auth0Config)

  const koraUser = ref<{
    trustLevel: string
    displayName: string
    email: string
    avatarUrl: string | null
  } | null>(null)
  /**
   * True once Kora has answered `KORA_ACCOUNT_CLOSED` for the signed-in token. The app shell routes
   * to `/account/closed` on this; the store has no router of its own.
   */
  const accountClosed = ref(false)
  /** True once Kora has answered `KORA_SIGNUP_NOT_INVITED` (KORA-148); the shell routes to `/account/not-invited`. */
  const signupNotInvited = ref(false)
  /** True once Kora has answered `KORA_EMAIL_NOT_VERIFIED` (KORA-152); the shell routes to `/account/verify-email`. */
  const emailNotVerified = ref(false)

  let initializationPromise: Promise<void> = Promise.resolve()

  // Initialize Auth0 immediately, but skip if we're on the callback page
  // (the router guard will handle the callback explicitly via handleRedirectCallback)
  if (typeof window !== 'undefined' && !window.location.pathname.includes('/callback')) {
    initializationPromise = typeof initialize === 'function' ? initialize() : Promise.resolve()
  }

  function waitForInitialization(): Promise<void> {
    return initializationPromise
  }

  // Fetch Kora user when authenticated – Kora DB is source of truth for trust level
  async function loadKoraUser(
    authenticated: boolean,
    wasAuthenticated: boolean | undefined
  ): Promise<void> {
    koraUser.value = null
    accountClosed.value = false
    signupNotInvited.value = false
    emailNotVerified.value = false
    // A different (or no) person is signed in: whatever deletion state we held is not theirs.
    useAccountDeletionStore().status = null
    // ADR-010 D3: signing out forgets the account language and its per-origin hint. The first
    // run (cold load, before Auth0 has answered) must not, or the hint would never be read.
    if (!authenticated) {
      if (wasAuthenticated) useAccountPreferencesStore().clear()
      return
    }
    try {
      const token = await getAccessToken()
      if (!token) return
      // Forward the live getter so each Kora request can refresh through Auth0.
      const client = createKoraClient({ getAccessToken })
      const profile = await client.getCurrentUser()
      // Language and time zone come from the same account fetch; the UI follows them (ADR-010 D3).
      useAccountPreferencesStore().apply(profile)
      koraUser.value = {
        trustLevel: profile.trustLevel,
        displayName: profile.displayName,
        email: profile.email,
        avatarUrl: profile.avatarUrl ?? null
      }
    } catch (cause) {
      // A closed account is final: say so instead of showing a signed-in app that cannot work.
      if (isAccountClosedError(cause)) accountClosed.value = true
      if (isSignupNotInvitedError(cause)) signupNotInvited.value = true
      if (isEmailNotVerifiedError(cause)) emailNotVerified.value = true
      // Otherwise fall back to ID claims if the Kora fetch fails (e.g. network, 404)
    }
  }

  /**
   * Settles when the Kora profile for the current sign-in has loaded or failed. Admin routes wait
   * on it: admin is the database trust level, which only the profile carries (KORA-165).
   */
  let koraUserLoad: Promise<void> = Promise.resolve()

  function waitForKoraUser(): Promise<void> {
    // One tick lets the sign-in watcher above start the load it is about to track.
    return nextTick().then(() => koraUserLoad)
  }

  watch(
    () => unref(isAuthenticated),
    (authenticated, wasAuthenticated) => {
      koraUserLoad = loadKoraUser(authenticated, wasAuthenticated)
    },
    { immediate: true }
  )

  // KORA-114: the one display-name rule (Kora profile, then Auth0 claims, then email) shared with
  // every product's account menu. Null when signed out or when there is no usable identity.
  const accountIdentity = computed<KoraAccountIdentity | null>(() =>
    resolveKoraAccountIdentity(koraUser.value, user.value)
  )

  const displayName = computed(() => {
    if (!user.value) return 'Guest'
    return accountIdentity.value?.displayName ?? 'User'
  })

  const userEmail = computed(() => user.value?.email ?? null)

  const trustLevel = computed(() => {
    if (koraUser.value !== null) return koraUser.value.trustLevel
    return user.value?.['https://architeia.org/trust_level'] || 'public'
  })

  // Admin is the Kora database trust level (KORA-20, KORA-165); token permissions grant nothing.
  const isAdmin = computed(() => isKoraAdminTrustLevel(trustLevel.value))

  // KORA-66: single flag for "we cannot determine auth state" — read by both the
  // router guard (fail-open redirect) and LoginView (skip the auto-login loop).
  const authUnavailable = computed(() => authConfigError.value !== null || initTimedOut.value)

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    displayName,
    accountIdentity,
    userEmail,
    trustLevel,
    isAdmin,
    getAccessToken,
    login,
    logout,
    handleRedirectCallback,
    initialize,
    waitForInitialization,
    waitForKoraUser,
    initTimedOut,
    authUnavailable,
    accountClosed,
    signupNotInvited,
    emailNotVerified
  }
})
